import type { ReadType } from '../ui/bits';
import type { FilterLists } from './filters';

// Shelves › Reference (Phase 6.2 Part 4; decisions doc › "Trying it (Kristi, Oct 26)" and
// "Shelves Part 4", Oct 28): By genre (biggest first, the grouping rule below) or A–Z (titles
// filed without a leading "The", "A" or "An"); covers or a list sorted by its headers.

export interface RefBook {
  id: string;
  label: number;
  title: string;
  author: string;
  /** The first author's surname, for sorting by author. */
  authorSort: string;
  cover: string | null;
  readType: ReadType | null;
  /** Genre and subgenre ids, in your order. */
  genres: string[];
  subgenres: string[];
  addedAt: string;
}

/** A title as it files: a leading "The", "A" or "An" skipped, lowercased (library practice). */
export function sortTitle(t: string): string {
  return t
    .trim()
    .replace(/^(the|a|an)\s+/i, '')
    .replace(/^["'“‘(\[]+/, '')
    .toLowerCase();
}

/** The letter a title (or a surname) files under; "#" for numbers and anything else. */
export function letterOf(s: string): string {
  const c = s.charAt(0).toUpperCase();
  return c >= 'A' && c <= 'Z' ? c : '#';
}

export const LETTERS = ['#', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'];

const byTitle = (a: RefBook, b: RefBook) => sortTitle(a.title).localeCompare(sortTitle(b.title)) || a.label - b.label;

/** The Cookbooks genre (yours may be called Cookbooks or Cooking). */
const isCooking = (g: { name: string; short: string | null }) => /^cook/i.test(g.name) || /^cook/i.test(g.short ?? '');

export interface RefGroup {
  /** A genre id, or "none" for books with no genre. */
  id: string;
  name: string;
  books: RefBook[];
  /** The group's own subgenres with their counts, in your Genres order. */
  subs: { id: string; name: string; count: number }[];
}

/**
 * By genre (Kristi, Oct 26): a book with Cookbooks among its genres goes under Cooking; any
 * other goes under the genre whose subgenres it has; ties (and books with no subgenre) go to
 * the genre most Reference books share. Groups biggest first; books A–Z inside each.
 * `all` is every Reference book (for "most shared"), `books` what's showing (after filters).
 */
export function groupByGenre(books: RefBook[], all: RefBook[], lists: FilterLists): RefGroup[] {
  const genre = new Map(lists.genres.map((g) => [g.id, g]));
  const subGenre = new Map(lists.subgenres.map((s) => [s.id, s.genreId]));
  const shared = new Map<string, number>();
  for (const b of all) for (const g of b.genres) shared.set(g, (shared.get(g) ?? 0) + 1);
  const groupOf = (b: RefBook): string => {
    const mine = b.genres.filter((g) => genre.has(g));
    if (!mine.length) return 'none';
    const cook = mine.find((g) => isCooking(genre.get(g)!));
    if (cook) return cook;
    const withSubs = mine.filter((g) => b.subgenres.some((s) => subGenre.get(s) === g));
    const pool = withSubs.length ? withSubs : mine;
    return pool.reduce((best, g) => ((shared.get(g) ?? 0) > (shared.get(best) ?? 0) ? g : best), pool[0]!);
  };
  const by = new Map<string, RefBook[]>();
  for (const b of books) {
    const g = groupOf(b);
    by.set(g, [...(by.get(g) ?? []), b]);
  }
  const order = new Map(lists.subgenres.map((s, i) => [s.id, i]));
  const out: RefGroup[] = [...by].map(([id, list]) => {
    const counts = new Map<string, number>();
    for (const b of list) for (const s of new Set(b.subgenres)) if (subGenre.get(s) === id) counts.set(s, (counts.get(s) ?? 0) + 1);
    return {
      id,
      name: id === 'none' ? 'No genre yet' : (genre.get(id)!.short ?? genre.get(id)!.name),
      books: [...list].sort(byTitle),
      subs: [...counts]
        .sort((a, b) => (order.get(a[0]) ?? 0) - (order.get(b[0]) ?? 0))
        .map(([s, count]) => ({ id: s, name: lists.subgenres.find((x) => x.id === s)!.name, count })),
    };
  });
  // Biggest first; "No genre yet" at the end.
  return out.sort((a, b) => (a.id === 'none' ? 1 : b.id === 'none' ? -1 : b.books.length - a.books.length || a.name.localeCompare(b.name)));
}

/** A–Z: every book by title as it files. */
export const azOrder = (books: RefBook[]) => [...books].sort(byTitle);

/** A cover's subgenre line: the subgenres it has under this genre (or any, in A–Z), at most two. */
export function subLine(b: RefBook, lists: FilterLists, genreId: string | null): string | undefined {
  const names = b.subgenres
    .map((s) => lists.subgenres.find((x) => x.id === s))
    .filter((s): s is FilterLists['subgenres'][number] => Boolean(s) && (!genreId || s!.genreId === genreId))
    .slice(0, 2)
    .map((s) => s.name);
  return names.length ? names.join(' · ') : undefined;
}

export type SortKey = 'title' | 'author' | 'genre' | 'subgenres';

/** The list view's column sort (Kristi, Oct 26: click a header to sort, again to reverse; empty values last). */
export function sortList(books: RefBook[], key: SortKey, desc: boolean, names: (b: RefBook, k: 'genre' | 'subgenres') => string): RefBook[] {
  const value = (b: RefBook) => (key === 'title' ? sortTitle(b.title) : key === 'author' ? b.authorSort : names(b, key).toLowerCase());
  return [...books].sort((a, b) => {
    const x = value(a);
    const y = value(b);
    if (!x !== !y) return x ? -1 : 1;
    return (desc ? -1 : 1) * x.localeCompare(y) || byTitle(a, b);
  });
}

/** The letter a book files under in the current sort (titles, or authors by surname). */
export const fileLetter = (b: RefBook, key: SortKey) => letterOf(key === 'author' ? b.authorSort : sortTitle(b.title));

/** A surname for sorting: the last word of the first author's name (Jr., Sr., II kept with it). */
export function surname(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length < 2) return name.toLowerCase();
  const last = parts[parts.length - 1]!;
  const s = /^(jr\.?|sr\.?|ii|iii|iv)$/i.test(last) && parts.length > 2 ? parts[parts.length - 2]! : last;
  return `${s} ${parts.join(' ')}`.toLowerCase();
}
